import React, { useCallback } from "react";
import { FieldValues, useController } from "react-hook-form";

import { IRadioGroupProps, RadioGroup } from "../input/RadioGroup";
import { FieldPathOfType, IControllerProps } from "./formTypes";

type IControlledRadioGroupProps<T extends string> = Omit<IRadioGroupProps<T>, "value">;

interface IProps<
    TOption extends string,
    TFieldValues extends FieldValues,
    TName extends FieldPathOfType<TFieldValues, TOption>,
> extends IControlledRadioGroupProps<TOption> {
    name: TName;
    controllerProps?: IControllerProps<TFieldValues, TName, TOption>;
    sx?: object;
}

export function ControllerRadioGroup<
    TOption extends string = string,
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPathOfType<TFieldValues, TOption> = FieldPathOfType<TFieldValues, TOption>,
>({
    name,
    controllerProps,
    onChange,
    sx,
    ...radioGroupProps
}: IProps<TOption, TFieldValues, TName>): React.ReactElement | null {
    const {
        field: { value, onChange: onFieldChange },
    } = useController({ ...controllerProps, name });

    const handleChange = useCallback(
        (evt: React.ChangeEvent<HTMLInputElement>, v: TOption): void => {
            onFieldChange(v);
            onChange?.(evt, v);
        },
        [onFieldChange, onChange],
    );

    return <MemoizedRadioGroup {...radioGroupProps} onChange={handleChange} value={value} sx={sx} />;
}

const MemoizedRadioGroup = React.memo(RadioGroup);
